import React from "react";
import { Agent } from "../../types/security";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TrendIndicator } from "./TrendIndicator";

interface AgentsListProps {
  agents: Agent[];
  onAgentClick: (agent: Agent) => void;
}

interface IssueCountProps {
  count: number;
  activeColor: string;
}

const IssueCount: React.FC<IssueCountProps> = ({ count, activeColor }) => {
  const colorClass = count > 0 ? activeColor : "text-gray-400";

  return <span className={`text-sm font-semibold ${colorClass}`}>{count}</span>;
};

interface AgentRowProps {
  agent: Agent;
  onAgentClick: (agent: Agent) => void;
}

const AgentRow: React.FC<AgentRowProps> = React.memo(({ agent, onAgentClick }) => {
  return (
    <TableRow
      className="cursor-pointer hover:bg-muted/50 transition-colors"
      onClick={() => onAgentClick(agent)}
    >
      <TableCell className="py-3">
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-medium leading-none">{agent.name}</span>
          <span className="text-xs text-muted-foreground leading-none">
            {agent.modelName}
          </span>
        </div>
      </TableCell>
      <TableCell className="py-3 max-w-xs">
        <span className="text-sm text-muted-foreground line-clamp-2 leading-tight">
          {agent.description}
        </span>
      </TableCell>
      <TableCell className="py-3 text-center">
        <IssueCount count={agent.criticalIssues} activeColor="text-red-600" />
      </TableCell>
      <TableCell className="py-3 text-center">
        <IssueCount count={agent.highIssues} activeColor="text-orange-600" />
      </TableCell>
      <TableCell className="py-3">
        <TrendIndicator direction={agent.trendDirection} percentage={agent.trendPercentage} />
      </TableCell>
    </TableRow>
  );
});

AgentRow.displayName = "AgentRow";

export const AgentsList: React.FC<AgentsListProps> = ({ agents, onAgentClick }) => {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow className="h-9">
            <TableHead className="py-2 text-xs font-medium">Agent</TableHead>
            <TableHead className="py-2 text-xs font-medium">Description</TableHead>
            <TableHead className="py-2 text-xs font-medium text-center">Critical</TableHead>
            <TableHead className="py-2 text-xs font-medium text-center">High</TableHead>
            <TableHead className="py-2 text-xs font-medium text-center">Trend</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {agents.map((agent) => (
            <AgentRow key={agent.id} agent={agent} onAgentClick={onAgentClick} />
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
